/* Share links and link-preview tags for the album page. */
import { Meta } from '@angular/platform-browser';

/** Query parameters that only steer this browser tab and must not travel in a shared link. */
const PRIVATE_PARAMS = ['ui', 'user_id', 'device', 'token'];

/**
 * Absolute link to a video of this album: the album URL with a single `?id=<video id>`.
 * (The classic page appended `?id=` to the full URL, which broke links that already had a query.)
 */
export function videoShareUrl(portal: string, routerUrl: string, videoId: any): string {
  const [path, query = ''] = (routerUrl || '').split('?');
  const params = new URLSearchParams(query);
  PRIVATE_PARAMS.forEach(name => params.delete(name));
  params.set('id', String(videoId));
  const base = (portal || '').replace(/\/$/, '');
  return `${base}${path}?${params.toString()}`;
}

export interface ShareTarget {
  label: string;
  href: string;
  icon: 'whatsapp' | 'facebook' | 'x' | 'linkedin' | 'email';
}

export function shareTargets(url: string, title: string): ShareTarget[] {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title || '');
  return [
    { label: 'WhatsApp', icon: 'whatsapp', href: `https://wa.me/?text=${t}%20${u}` },
    { label: 'Facebook', icon: 'facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { label: 'X', icon: 'x', href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
    { label: 'LinkedIn', icon: 'linkedin', href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { label: 'Email', icon: 'email', href: `mailto:?subject=${t}&body=${u}` },
  ];
}

/** Same embed code the classic share popup produced. */
export function embedCode(url: string): string {
  return `<iframe src="${url}" width="560" height="315" frameborder="0" allowfullscreen></iframe>`;
}

export interface SharePreview {
  title: string;
  description?: string;
  image?: string | null;
  url?: string;
}

/**
 * Open Graph / Twitter tags for link previews. Crawlers that do not run JavaScript only see
 * these once the page is server-rendered; apps that render the page pick them up today.
 */
export function sharePreviewTags(preview: SharePreview): { name: string; content: string }[] {
  const tags = [
    { name: 'og:type', content: 'video.other' },
    { name: 'og:title', content: preview.title || '' },
    { name: 'twitter:title', content: preview.title || '' },
    { name: 'twitter:card', content: preview.image ? 'summary_large_image' : 'summary' },
  ];
  if (preview.description) tags.push({ name: 'og:description', content: preview.description }, { name: 'twitter:description', content: preview.description });
  if (preview.image) tags.push({ name: 'og:image', content: preview.image }, { name: 'twitter:image', content: preview.image });
  if (preview.url) tags.push({ name: 'og:url', content: preview.url });
  return tags;
}

/** The album page itself (no query), for og:url. */
export function albumShareUrl(portal: string, routerUrl: string): string {
  return (portal || '').replace(/\/$/, '') + (routerUrl || '').split('?')[0];
}

/** Writes the preview tags: og:* as property, twitter:* as name. */
export function applySharePreview(meta: Meta, preview: SharePreview): void {
  sharePreviewTags(preview).forEach(tag => tag.name.startsWith('og:')
    ? meta.updateTag({ property: tag.name, content: tag.content }, `property="${tag.name}"`)
    : meta.updateTag({ name: tag.name, content: tag.content }));
}

export interface AnchorBox { top: number; bottom: number; right: number; }
export interface Size { width: number; height: number; }

/** Where a popover goes next to its button: below it (above when only that fits), right edges aligned, inside the viewport. */
export function anchoredPosition(anchor: AnchorBox, size: Size, viewport: Size, gap = 8, margin = 16): { top: number; left: number; above: boolean } {
  const left = Math.max(margin, Math.min(anchor.right - size.width, viewport.width - size.width - margin));
  const fitsBelow = anchor.bottom + gap + size.height <= viewport.height - margin;
  const above = !fitsBelow && anchor.top - gap - size.height >= margin;
  const top = above ? anchor.top - gap - size.height
    : Math.max(margin, Math.min(anchor.bottom + gap, viewport.height - size.height - margin));
  return { top, left, above };
}
